import * as React from 'react';
import { currencies } from '@/enums';
import { getCurrencyConversions } from '@/redux/modules/currency';
import { getItemSummaryCurrency, getItemSummaryStartPrice } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveCatalogCurrentItemId, getLiveItemDataLeadingBid } from '@/redux/modules/console';
import { preferredCurrencySelector } from '@/redux/modules/bidder/preferences/bidderPreferences.selectors';
import { useAppSelector } from '@/redux/hooks';
import caseInsensitiveCompare from '@liveauctioneers/caterwaul-components/lib/utils/caseInsensitiveCompare';
import type { CurrencyConversion } from '@/types/Currency';

type HookParams = {
    catalogId: number;
    selectedCurrency: string;
};

type UseCurrencyConversion = {
    showConversion: boolean;
    targetCurrencyCode: string;
    targetCurrencyConversion: CurrencyConversion;
    valueInTargetCurrency: number;
};

const useCurrencyConversion = ({ catalogId, selectedCurrency }: HookParams): UseCurrencyConversion => {
    const itemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const leadingBid = useAppSelector((state) => getLiveItemDataLeadingBid(state, itemId));

    const conversions = useAppSelector(getCurrencyConversions);
    const preferredCurrency = useAppSelector(preferredCurrencySelector);

    const currentBid = leadingBid || startPrice;

    const { showConversion, targetCurrencyCode, targetCurrencyConversion, valueInTargetCurrency } =
        React.useMemo(() => {
            let targetCurrencyCode = selectedCurrency;
            if (!Boolean(targetCurrencyCode)) {
                if (Boolean(preferredCurrency) && preferredCurrency !== currencies.NoCurrency) {
                    targetCurrencyCode = preferredCurrency;
                } else {
                    targetCurrencyCode = currencies.USD;
                }
            }

            const defaultConversion: CurrencyConversion = {
                conversionToUsd: 0,
                currencyCode: 'USD',
                fractionSize: 2,
                symbol: '$',
                updated: '',
            };

            const sourceCurrencyConversion =
                conversions.find((c) => caseInsensitiveCompare(c.currencyCode, currency)) || defaultConversion;
            const targetCurrencyConversion =
                conversions.find((c) => caseInsensitiveCompare(c.currencyCode, targetCurrencyCode)) ||
                defaultConversion;

            const showConversion = !caseInsensitiveCompare(currency, targetCurrencyCode);

            // Convert source currency to USD, we do this because we only store conversions from usd, so we always need this.
            const sourceInUSD = currentBid / sourceCurrencyConversion.conversionToUsd;

            // Convert from USD to target currency
            const valueInTargetCurrency = sourceInUSD * targetCurrencyConversion.conversionToUsd;

            return {
                showConversion,
                targetCurrencyCode,
                targetCurrencyConversion,
                valueInTargetCurrency,
            };
        }, [conversions, currentBid, currency, preferredCurrency, selectedCurrency]);

    return {
        showConversion,
        targetCurrencyCode,
        targetCurrencyConversion,
        valueInTargetCurrency,
    };
};

export default useCurrencyConversion;
